'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { useRouter, usePathname } from 'next/navigation';
import { Loader2, ShieldCheck, Zap, Globe, Users, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { useToast } from '@/doi-tac-module/hooks/use-toast';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import {
    signInWithPopup,
    GoogleAuthProvider,
    FacebookAuthProvider,
    getIdToken,
    signInWithEmailAndPassword
} from 'firebase/auth';
import { auth } from '@/doi-tac-module/lib/firebase/config';

const GoogleIcon = () => (
    <svg className="!h-[17px] !w-[17px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
        <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
        <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path>
        <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.574l6.19,5.238C39.99,34.546,44,29.836,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
    </svg>
);

const FacebookIcon = () => (
    <svg className="!h-[18px] !w-[18px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
        <path fill="#039be5" d="M24 5A19 19 0 1 0 24 43A19 19 0 1 0 24 5Z"></path>
        <path fill="#fff" d="M26.572,29.036h4.917l0.772-4.995h-5.69v-2.73c0-2.075,0.678-3.915,2.619-3.915h3.119v-4.359c-0.548-0.074-1.707-0.236-3.897-0.236c-4.573,0-7.254,2.415-7.254,7.917v3.323h-4.701v4.995h4.701v12.022C24.529,41.938,25.539,41.974,26.572,41.974z"></path>
    </svg>
);

export function AgencyJoinInvitation() {
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const router = useRouter();
    const pathname = usePathname();
    const { toast } = useToast();
    const { language } = useLanguage();
    const t = agencyTranslations.joinInvitation;

    const syncSession = async (user: any, extraData?: any) => {
        const idToken = await getIdToken(user);
        const response = await fetch("/api/login", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
            }
        });

        if (!response.ok) {
            throw new Error("Failed to sync session with server");
        }
    };

    const handleGoogleLogin = async () => {
        setIsLoading(true);
        const provider = new GoogleAuthProvider();
        provider.addScope('email');
        provider.addScope('profile');
        try {
            const userCredential = await signInWithPopup(auth, provider);
            await syncSession(userCredential.user);
            toast({
                title: t.toast.success.title[language],
                description: t.toast.success.desc[language],
                className: 'bg-[#AFC536] text-white',
                duration: 2000,
            });
            router.refresh();
        } catch (error: any) {
            if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
                console.error("Google login error:", error);
            }
            toast({
                variant: 'destructive',
                title: t.toast.error.title[language],
                description: t.toast.error.google[language],
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFacebookLogin = async () => {
        setIsLoading(true);
        const provider = new FacebookAuthProvider();
        try {
            const userCredential = await signInWithPopup(auth, provider);
            await syncSession(userCredential.user);
            toast({
                title: t.toast.success.title[language],
                description: t.toast.success.desc[language],
                className: 'bg-[#AFC536] text-white',
                duration: 2000,
            });
            router.refresh();
        } catch (error: any) {
            if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
                console.error("Facebook login error:", error);
            }
            toast({
                variant: 'destructive',
                title: t.toast.error.title[language],
                description: t.toast.error.facebook[language],
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailLogin = async (e?: React.FormEvent) => {
        // Disabled based on user request to only allow Google/Facebook
        return;
    };

    return (
        <div className="container mx-auto px-4 py-10 md:py-16 min-h-[calc(100vh-64px)] flex flex-col items-center justify-center max-w-6xl">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col lg:flex-row border border-slate-100 lg:min-h-[650px]">
                {/* Left Side: Invitation Content */}
                <div className="flex-1 p-8 md:p-12 md:px-14 bg-gradient-to-br from-slate-50 to-white flex flex-col justify-center">
                    <div className="mb-6">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F2B92A]/10 text-[#F2B92A] text-sm font-bold mb-3">
                            <Zap className="h-4 w-4" />
                            {t.badge[language]}
                        </div>
                        <h1 className="text-2xl md:text-2xl font-bold font-headline mb-2 text-slate-900 leading-tight">
                            {t.title[language]}
                        </h1>
                        <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-6">
                            {t.subtitle[language]}
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div className="flex items-start gap-2 p-3 rounded-xl bg-white border border-slate-100 shadow-sm">
                                <ShieldCheck className="h-6 w-6 text-[#AFC536] shrink-0" />
                                <div>
                                    <h4 className="font-bold text-slate-900">{t.features.trust.title[language]}</h4>
                                    <p className="text-sm text-slate-500">{t.features.trust.desc[language]}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-2 p-3 rounded-xl bg-white border border-slate-100 shadow-sm">
                                <Globe className="h-6 w-6 text-[#19A6DF] shrink-0" />
                                <div>
                                    <h4 className="font-bold text-slate-900">{t.features.market.title[language]}</h4>
                                    <p className="text-sm text-slate-500">{t.features.market.desc[language]}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-4 max-w-md mx-auto lg:mx-0 pt-6">
                        <Button
                            variant="outline"
                            className="justify-center h-14 text-base md:text-lg font-medium border-slate-200 hover:bg-slate-50 hover:border-[#039be5] hover:text-[#039be5] transition-all px-8 shadow-sm rounded-2xl gap-3 group"
                            onClick={handleFacebookLogin}
                            disabled={isLoading}
                        >
                            <FacebookIcon />
                            <span>{t.buttons.facebook[language]}</span>
                        </Button>

                        <Button
                            variant="outline"
                            className="justify-center h-14 text-base md:text-lg font-medium border-slate-200 hover:bg-slate-50 hover:border-[#F2B92A] hover:text-[#F2B92A] transition-all px-8 shadow-sm rounded-2xl gap-3 group"
                            onClick={handleGoogleLogin}
                            disabled={isLoading}
                        >
                            {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-[#F2B92A]" /> : <GoogleIcon />}
                            <span>{t.buttons.google[language]}</span>
                        </Button>
                    </div>

                    <p className="mt-6 text-sm text-slate-400">
                        {t.terms.prefix[language]}{' '}
                        <a href="#" className="text-[#F2B92A] hover:underline font-medium">{t.terms.service.agency[language]}</a>
                        {' '} {t.terms.and[language]} {' '}
                        <a href="#" className="text-[#F2B92A] hover:underline font-medium">{t.terms.privacy[language]}</a>.
                    </p>
                </div>

                {/* Right Side: Visual Image */}
                <div className="hidden lg:block w-[38%] relative bg-slate-900 overflow-hidden">
                    <Image
                        src="/img/BANGLADESH/TRANGVIETNAM/sso doi-tac.jpg"
                        alt="HelloJob Partnership"
                        fill
                        className="object-cover"
                        priority
                    />
                    {/* Gradient Overlay for better text readability - Bottom Only */}
                    <div className="absolute bottom-0 left-0 right-0 h-[40%] bg-gradient-to-t from-[#FF6921]/70 via-[#FF6921]/30 to-transparent" />
                    <div className="absolute bottom-12 left-8 right-8 text-white drop-shadow-2xl">
                        <Users className="h-10 w-10 mb-4 text-[#F2B92A]" />
                        <h3 className="text-xl font-bold font-headline mb-2 leading-tight">
                            {t.visual.title[language]}
                        </h3>
                        <p className="text-white/70">
                            {t.visual.desc[language]}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
